import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Check, Image as ImageIcon, RefreshCw, Trash2, Upload } from 'lucide-react'
import { programsApi, type ProgramThumbnails as Thumbnails } from '../../lib/api.js'
import { formatDuration } from '../../lib/utils.js'
import { Spinner } from '../common/Spinner.js'

interface ProgramThumbnailsProps {
  programId: string
  /** Prévient la fiche parente : la vignette retenue a changé. */
  onChanged?: () => void
  compact?: boolean
}

/**
 * Miniatures d'un programme, sous le média associé.
 *
 * Trois candidates prélevées à 10, 50 et 90 % de la durée — ni le tout début
 * ni la toute fin, qui donnent souvent du noir ou un fondu. L'opérateur peut
 * aussi importer sa propre image. Les fichiers vivent dans un dossier servi par
 * le backend, à l'écart du dossier média que CasparCG parcourt.
 */
export const ProgramThumbnails = memo(function ProgramThumbnails({
  programId, onChanged, compact = false,
}: ProgramThumbnailsProps) {
  const [state, setState] = useState<Thumbnails>({ selectedUrl: null, candidates: [] })
  const [busy, setBusy] = useState<'load' | 'generate' | 'import' | null>(null)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    setBusy('load')
    try {
      setState(await programsApi.listThumbnails(programId))
      setError('')
    } catch {
      setError('Miniatures indisponibles')
    } finally {
      setBusy(null)
    }
  }, [programId])

  useEffect(() => { void load() }, [load])

  const handleGenerate = useCallback(async () => {
    setBusy('generate')
    setError('')
    try {
      setState(await programsApi.generateThumbnails(programId))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Génération impossible')
    } finally {
      setBusy(null)
    }
  }, [programId])

  const handleSelect = useCallback(async (url: string) => {
    const previous = state.selectedUrl
    // Bascule : recliquer la vignette retenue la retire.
    const next = previous === url ? null : url
    setState(current => ({ ...current, selectedUrl: next }))
    try {
      setState(await programsApi.selectThumbnail(programId, next))
      onChanged?.()
    } catch (cause) {
      setState(current => ({ ...current, selectedUrl: previous }))
      setError(cause instanceof Error ? cause.message : 'Sélection impossible')
    }
  }, [onChanged, programId, state.selectedUrl])

  const handleImportClick = useCallback(() => fileInputRef.current?.click(), [])
  const handleImport = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setBusy('import')
    setError('')
    try {
      setState(await programsApi.importThumbnail(programId, file))
      onChanged?.()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Import impossible')
    } finally {
      setBusy(null)
    }
  }, [onChanged, programId])

  const handleClear = useCallback(async () => {
    if (!window.confirm('Retirer les miniatures de ce programme ?')) return
    try {
      await programsApi.clearThumbnails(programId)
      setState({ selectedUrl: null, candidates: [] })
      onChanged?.()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Suppression impossible')
    }
  }, [onChanged, programId])

  const visibleCandidates = compact
    ? state.candidates.filter(candidate => candidate.url === state.selectedUrl).slice(0, 1)
    : state.candidates

  return (
    <section className={`program-thumbs${compact ? ' program-thumbs--compact' : ''}`} aria-label="Miniatures">
      <div className="program-thumbs__header">
        <h3 className="program-thumbs__title">
          <ImageIcon size={14} aria-hidden="true" />
          {compact ? 'Miniature sélectionnée' : 'Miniatures'}
        </h3>
        <div className="program-thumbs__actions">
          <button
            type="button"
            className="tp-button"
            onClick={handleGenerate}
            disabled={busy !== null}
            title="Prélever trois images dans le média associé"
          >
            <RefreshCw size={13} aria-hidden="true" />
            {busy === 'generate' ? 'Génération…' : compact ? 'Remplacer' : 'Générer 3 miniatures'}
          </button>
          <button
            type="button"
            className="tp-button"
            onClick={handleImportClick}
            disabled={busy !== null}
            title="Importer une image depuis le poste"
          >
            <Upload size={13} aria-hidden="true" />
            {busy === 'import' ? 'Import…' : compact ? 'Importer une image' : 'Importer'}
          </button>
          {state.candidates.length > 0 && (
            <button
              type="button"
              className="tp-icon-button"
              onClick={handleClear}
              disabled={busy !== null}
              title="Retirer les miniatures"
              aria-label="Retirer les miniatures"
            >
              <Trash2 size={13} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImport}
        className="program-thumbs__file"
        aria-hidden="true"
        tabIndex={-1}
      />

      {error && <div className="tp-feedback tp-feedback--error program-thumbs__error">{error}</div>}

      {visibleCandidates.length === 0 ? (
        <p className="program-thumbs__empty">
          {busy === 'load'
            ? <><Spinner /> Chargement…</>
            : compact ? 'Aucune miniature sélectionnée pour ce programme.' : 'Aucune miniature. Générez-en depuis le média associé, ou importez une image.'}
        </p>
      ) : (
        <div className="program-thumbs__grid">
          {visibleCandidates.map(candidate => {
            const selected = candidate.url === state.selectedUrl
            return (
              <button
                key={candidate.url}
                type="button"
                className={`program-thumb${selected ? ' is-selected' : ''}`}
                onClick={() => { void handleSelect(candidate.url) }}
                title={selected ? 'Retirer cette miniature' : 'Retenir cette miniature'}
                aria-pressed={selected}
              >
                <img src={candidate.url} alt="" loading="lazy" />
                {selected && (
                  <span className="program-thumb__badge" aria-hidden="true">
                    <Check size={11} />
                    RETENUE
                  </span>
                )}
                <span className="program-thumb__meta">
                  {candidate.origin === 'imported'
                    ? 'Importée'
                    : candidate.atSec > 0 ? formatDuration(candidate.atSec) : 'Prélevée'}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </section>
  )
})
